import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNewTeamMemberInvitationMutation } from "../../../service/team-management-service";
import { useLazyGetRolesQuery } from "../../../service/role-service";
import { Role } from "../../../model/roles/role";
import { FullAccessRoleIcon, CanPayBillsRoleIcon, ViewerRoleIcon } from "../../../assets/icons";
import styles from "./shared-account.module.scss";

interface Props {
	isOpen: boolean;
	onClose: () => void;
}

interface RoleMeta {
	icon: React.ReactNode;
	desc: string;
}

const getRoleMeta = (name: string): RoleMeta => {
	const n = name.toLowerCase();
	if (n.includes("full") || n.includes("admin")) {
		return { icon: <FullAccessRoleIcon />, desc: "Complete control over account settings, billing, and user permissions." };
	}
	if (n.includes("pay") || n.includes("bill")) {
		return { icon: <CanPayBillsRoleIcon />, desc: "Can manage payments, buy energy units, and view full account details." };
	}
	if (n.includes("view") || n.includes("read")) {
		return { icon: <ViewerRoleIcon />, desc: "Read-only access to view consumption data and account details." };
	}
	return { icon: <ViewerRoleIcon />, desc: "Access to selected account features." };
};

// ── Role Picker ───────────────────────────────────────────────────────────────
interface RolePickerProps {
	roles: Role[];
	selectedId: string;
	onChange: (id: string) => void;
}

const RolePicker: React.FC<RolePickerProps> = ({ roles, selectedId, onChange }) => {
	const [open, setOpen] = useState(false);
	const selected = roles.find(r => r.id === selectedId);
	const meta = selected ? getRoleMeta(selected.name) : null;

	const handleSelect = (id: string) => {
		onChange(id);
		setOpen(false);
	};

	return (
		<div className={styles.rolePicker}>
			<button type="button" className={styles.rolePickerTrigger} onClick={() => setOpen(v => !v)}>
				<div className={`${styles.roleIconCircle} ${!selected ? styles.roleIconCircleMuted : ""}`}>
					{meta ? meta.icon : (
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
							<path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
						</svg>
					)}
				</div>
				<div className={styles.roleInfo}>
					<span className={selected ? styles.roleOptionName : `${styles.roleOptionName} ${styles.roleOptionNameMuted}`}>
						{selected ? selected.name : "Select a role"}
					</span>
					<span className={styles.roleOptionDesc}>
						{meta ? meta.desc : "Choose what this member is allowed to see and manage on the account."}
					</span>
				</div>
				<svg
					className={`${styles.roleChevron} ${open ? styles.roleChevronOpen : ""}`}
					width="16" height="16" viewBox="0 0 24 24" fill="none"
				>
					<path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</button>

			{open && (
				<div className={styles.roleList}>
					{roles.map(role => {
						const m = getRoleMeta(role.name);
						const isSelected = role.id === selectedId;
						return (
							<button
								key={role.id}
								type="button"
								className={`${styles.roleOption} ${isSelected ? styles.roleOptionSelected : ""}`}
								onClick={() => handleSelect(role.id)}
							>
								<div className={styles.roleIconCircle}>{m.icon}</div>
								<div className={styles.roleInfo}>
									<span className={styles.roleOptionName}>{role.name}</span>
									<span className={styles.roleOptionDesc}>{m.desc}</span>
								</div>
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

// ── Modal ─────────────────────────────────────────────────────────────────────
export const InviteTeamMemberModal: React.FC<Props> = ({ isOpen, onClose }) => {
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [roleId, setRoleId] = useState("");
	const [roles, setRoles] = useState<Role[]>([]);

	const [inviteMember, { isLoading }] = useNewTeamMemberInvitationMutation();
	const [getRoles] = useLazyGetRolesQuery();

	useEffect(() => {
		if (isOpen && roles.length === 0) {
			getRoles({ Page: 1, PageSize: 50 }).then(result => {
				if (result.data?.items) setRoles(result.data.items);
			});
		}
	}, [isOpen, getRoles, roles.length]);

	useEffect(() => {
		if (!isOpen) {
			setFirstName("");
			setLastName("");
			setEmail("");
			setRoleId("");
		}
	}, [isOpen]);

	const handleSubmit = async () => {
		if (!firstName || !lastName || !email || !roleId) {
			toast.error("Please fill in all fields");
			return;
		}
		try {
			await inviteMember({ email, firstName, lastName, roleId }).unwrap();
			toast.success("Invitation sent successfully");
			onClose();
		} catch (err: any) {
			toast.error(err?.data?.title || "Failed to send invitation");
		}
	};

	if (!isOpen) return null;

	return (
		<div className={styles.modalOverlay} onClick={onClose}>
			<div className={styles.modalSheet} onClick={e => e.stopPropagation()}>
				<div className={styles.modalHeader}>
					<span className={styles.modalTitle}>Invite Member</span>
					<button className={styles.modalClose} onClick={onClose}>✕</button>
				</div>

				<div className={styles.modalBody}>
					<div className={styles.fieldGroup}>
						<span className={styles.fieldLabel}>Email address</span>
						<input className={styles.cardInput} type="email" placeholder="mail@address.com" value={email} onChange={e => setEmail(e.target.value)} />
					</div>
					<div className={styles.fieldGroup}>
						<span className={styles.fieldLabel}>First name</span>
						<input className={styles.cardInput} placeholder="e.g. John" value={firstName} onChange={e => setFirstName(e.target.value)} />
					</div>
					<div className={styles.fieldGroup}>
						<span className={styles.fieldLabel}>Surname</span>
						<input className={styles.cardInput} placeholder="e.g. Markodi" value={lastName} onChange={e => setLastName(e.target.value)} />
					</div>
					<div className={styles.fieldGroup}>
						<span className={styles.fieldLabel}>Assign Role</span>
						<RolePicker roles={roles} selectedId={roleId} onChange={setRoleId} />
					</div>
				</div>

				<button className={styles.continueButton} onClick={handleSubmit} disabled={isLoading}>
					{isLoading ? "Sending..." : "Send Invitation"}
				</button>
			</div>
		</div>
	);
};
